/**
 * VersionStamp — tiny fixed-position version label at bottom-left.
 *
 *   - Click           → copy `v<APP_VERSION>` to clipboard (great for bug reports)
 *   - Right-click     → open hidden debug menu (self-service troubleshooting).
 *                       On touch devices, a long-press triggers the same
 *                       `contextmenu` event, so the menu is reachable on
 *                       phones without any extra wiring.
 *
 * Why a hidden menu instead of a visible button: keeps the user-facing UI
 * clean, but gives you (and savvy users you direct via support) one-click
 * access to the "did you try clearing the cache?" actions that otherwise
 * require DevTools knowledge.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Send } from 'lucide-react';
import { APP_VERSION } from '../lib/appVersion';
import { buildSupportMailto, getDiagnosticSnapshot } from '../lib/diagnostics';

const NEUROWAVE_LS_PREFIX = 'neurowave_';
// Where "Send diagnostic snapshot" addresses its mail. Kept in sync with
// the BrainwaveGenerator's existing support-mail link.
const SUPPORT_EMAIL = 'selfheal222@msn.com';

// Shared "send the diagnostic snapshot to the backend, fall back to mailto"
// helper — called by both the visible quick-send button next to the stamp
// AND the long-press debug menu's "Send anonymously" action. Returns a
// short status string suitable for inline UI feedback.
const sendDiagnosticAnonymous = async () => {
  try {
    const snapshot = getDiagnosticSnapshot();
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${backend}/api/diagnostics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ snapshot, app_version: APP_VERSION }),
      // No credentials — this endpoint is intentionally anonymous.
    });
    if (res.ok) return 'Sent — thanks!';
    if (res.status === 429) return 'Rate limited — try again in a moment.';
    throw new Error(`server returned ${res.status}`);
  } catch (err) {
    // Network or backend failure — degrade gracefully to the mailto path.
    try {
      window.location.href = buildSupportMailto(SUPPORT_EMAIL);
      return 'Opened email as fallback.';
    } catch {
      return `Send failed: ${err.message || err}`;
    }
  }
};

// Debug actions, defined outside the component so the dropdown items are
// trivially extensible — add an object, get a new menu row.
const DEBUG_ACTIONS = [
  {
    id: 'force-update-check',
    label: 'Force update check',
    description: 'Ask the service worker to look for a new build now.',
    run: async () => {
      if (!('serviceWorker' in navigator)) {
        return 'Service worker not supported in this browser.';
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return 'No service worker registered yet.';
      await reg.update();
      return reg.waiting
        ? 'New version waiting — close the menu, banner should appear.'
        : 'Up to date.';
    },
  },
  {
    id: 'clear-offline-cache',
    label: 'Clear offline cache',
    description: 'Delete all cached app shell + preset data, then reload.',
    run: async () => {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      window.location.reload();
      return 'Reloading…';
    },
  },
  {
    id: 'reset-session-storage',
    label: 'Reset all NeuroWave storage',
    description: 'Wipes only neurowave_* keys, then reloads. Auth cookie stays.',
    run: async () => {
      try {
        const keys = Object.keys(localStorage).filter((k) =>
          k.startsWith(NEUROWAVE_LS_PREFIX)
        );
        keys.forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore */ }
      window.location.reload();
      return 'Reloading…';
    },
  },
  {
    id: 'send-diagnostic-snapshot',
    label: 'Send diagnostic snapshot',
    description: 'Opens your mail client with version + environment + recent log entries pre-filled.',
    run: async () => {
      try {
        // Best-effort clipboard backup: many mail clients on iOS truncate
        // long mailto bodies. Putting the snapshot on the clipboard means
        // the user can paste it back in if their mail client mangles it.
        const snapshot = getDiagnosticSnapshot();
        try { await navigator.clipboard.writeText(snapshot); } catch { /* ignore */ }
        const href = buildSupportMailto(SUPPORT_EMAIL);
        window.location.href = href;
        return 'Mail client opening — snapshot also copied to clipboard.';
      } catch (err) {
        return `Could not build snapshot: ${err.message || err}`;
      }
    },
  },
  {
    id: 'send-diagnostic-anon',
    label: 'Send anonymously (no mail client)',
    description: 'POSTs the snapshot directly to support — fastest path, no app-switch.',
    run: sendDiagnosticAnonymous,
  },
];

export const VersionStamp = () => {
  const { pathname } = useLocation();
  const onGeneratorPage = pathname.startsWith('/app');
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Inline status per-action ("Running…" / result string). Cleared when menu re-opens.
  const [status, setStatus] = useState({});
  const rootRef = useRef(null);

  // Close menu on outside click or Esc — standard popover hygiene.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleStampClick = async (e) => {
    // Plain left-click: copy to clipboard. Right-click (handled separately
    // via onContextMenu) opens the debug menu.
    try {
      await navigator.clipboard.writeText(`v${APP_VERSION}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore: insecure context / unsupported */ }
  };

  // Right-click / long-press → open debug menu, suppress the browser's
  // native context menu just on this tiny element so the rest of the page
  // still gets normal right-click behaviour.
  const handleStampContextMenu = (e) => {
    e.preventDefault();
    setStatus({});
    setMenuOpen((v) => !v);
  };

  const runAction = async (action) => {
    setStatus((s) => ({ ...s, [action.id]: 'Running…' }));
    try {
      const result = await action.run();
      setStatus((s) => ({ ...s, [action.id]: result || 'Done.' }));
    } catch (err) {
      setStatus((s) => ({ ...s, [action.id]: `Error: ${err.message || err}` }));
    }
  };

  // Status text shown next to the visible "Send diagnostic" link.
  // Lives next to the version stamp so users don't have to dig into the
  // long-press debug menu — one tap, one trace.
  const [quickSendStatus, setQuickSendStatus] = useState(null);
  const handleQuickSend = async (e) => {
    // Stop the click from bubbling into handleStampClick (which would copy
    // the version to clipboard) — these are sibling buttons.
    e.stopPropagation();
    setQuickSendStatus('Sending…');
    const msg = await sendDiagnosticAnonymous();
    setQuickSendStatus(msg);
    // Auto-clear after 4s so the chrome stays minimal.
    setTimeout(() => setQuickSendStatus(null), 4000);
  };

  return (
    <div ref={rootRef} className={`fixed ${onGeneratorPage ? 'bottom-[4.5rem] md:bottom-2 bg-black/50 backdrop-blur-sm rounded-md md:bg-transparent md:backdrop-blur-none' : 'bottom-2'} left-2 z-[40] flex items-center gap-1`}>
      <button
        type="button"
        onClick={handleStampClick}
        onContextMenu={handleStampContextMenu}
        data-testid="version-stamp"
        title="Click to copy · Right-click (or long-press) for debug menu"
        aria-label={`App version ${APP_VERSION}, click to copy, right-click for debug menu`}
        className="px-1.5 py-0.5 rounded text-[10px] font-mono tracking-tight text-white/25 hover:text-white/70 hover:bg-white/5 transition-colors select-none"
      >
        {copied ? 'copied' : `v${APP_VERSION}`}
      </button>
      <button
        type="button"
        onClick={handleQuickSend}
        data-testid="version-stamp-quick-send"
        title="Send anonymous diagnostic snapshot"
        aria-label="Send anonymous diagnostic snapshot"
        className="px-1 py-0.5 rounded text-white/25 hover:text-white/70 hover:bg-white/5 transition-colors"
      >
        <Send className="w-2.5 h-2.5" />
      </button>
      {quickSendStatus && (
        <span
          className="text-[10px] font-mono text-white/55 px-1"
          data-testid="version-stamp-quick-send-status"
        >
          {quickSendStatus}
        </span>
      )}

      {menuOpen && (
        <div
          role="menu"
          aria-label="Debug actions"
          data-testid="version-stamp-debug-menu"
          className="absolute bottom-full left-0 mb-1 w-72 rounded-lg border border-white/10 bg-[#0a0e1a]/95 backdrop-blur-md shadow-2xl shadow-black/50 p-1.5"
        >
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-white/40 font-mono border-b border-white/5 mb-1">
            Debug — v{APP_VERSION}
          </div>
          {DEBUG_ACTIONS.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              onClick={() => runAction(action)}
              data-testid={`debug-${action.id}`}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 transition-colors"
            >
              <div className="text-[12px] text-white/85">{action.label}</div>
              <div className="text-[10px] text-white/40 leading-tight mt-0.5">
                {status[action.id] || action.description}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default VersionStamp;
